/**
 * GA4PageViews — fires a `page_view` event on every React Router
 * navigation. Mount ONCE inside <BrowserRouter> (outside <Routes>) so
 * it captures every top-level path change, including the Client Portal
 * tree (`/portal/*`). The initial gtag config in public/index.html sets
 * `send_page_view: false`, so this component owns page_view emission
 * end-to-end — no double-counting on the first load.
 *
 * Renders nothing.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// GA4 measurement ID hard-coded per product spec. If we ever add a
// staging property, promote this to REACT_APP_GA4_ID with a fallback.
const GA4_MEASUREMENT_ID = "G-17RXRHNL5S";

export default function GA4PageViews() {
  const location = useLocation();
  // Track the last path we reported so React strict-mode double-invokes
  // in dev don't fire the same page_view twice.
  const lastPath = useRef(null);

  useEffect(() => {
    // gtag is loaded async — if it isn't ready yet, queue via dataLayer
    // (the shim in index.html pushes onto dataLayer before the CDN
    // script arrives, and GA4 will drain it on load).
    const gtag = window.gtag || function () { (window.dataLayer = window.dataLayer || []).push(arguments); };

    const path = location.pathname + location.search;
    if (path === lastPath.current) return;
    lastPath.current = path;

    // Which surface is this — agent CRM or client portal — so we can
    // segment funnels in GA4 without joining paths client-side.
    const surface = location.pathname.startsWith("/portal") ? "client_portal" : "agent_crm";

    gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      send_to: GA4_MEASUREMENT_ID,
      surface,
    });
  }, [location.pathname, location.search]);

  return null;
}
